import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ShieldCheck, ArrowLeft, ShieldAlert } from "lucide-react";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import Logo from "../components/Logo";

const TYPE_LABELS = {
  prescription: "Receta médica",
  exam_order: "Petición de exámenes",
  certificate: "Certificado médico",
};

const CERT_TYPE_LABELS = {
  baja: "Baja médica (Incapacidad Temporal)",
  alta: "Alta médica",
  justificante: "Justificante de asistencia",
  ordinario: "Certificado ordinario",
  aptitud: "Certificado de aptitud",
  informe: "Informe médico",
};

function docSubtitle(d) {
  if (!d) return "";
  if (d.type === "prescription") return TYPE_LABELS.prescription;
  if (d.type === "exam_order")
    return `${TYPE_LABELS.exam_order}${d.exam_type ? ` (${d.exam_type === "lab" ? "Laboratorio" : "Imagen"})` : ""}`;
  if (d.type === "certificate")
    return `${TYPE_LABELS.certificate}${d.cert_type ? ` · ${CERT_TYPE_LABELS[d.cert_type] || d.cert_type}` : ""}`;
  return TYPE_LABELS[d.type] || "Documento firmado";
}

function invalidReasonLabel(reason) {
  switch (reason) {
    case "not_found": return "Código no encontrado";
    case "code_mismatch": return "El código de verificación no coincide";
    case "verification_code_required": return "Falta el parámetro de verificación (v)";
    case "signature_invalid": return "La firma electrónica no es válida: el documento ha sido alterado";
    case "unsigned": return "El documento no está firmado electrónicamente";
    default: return "Documento no válido";
  }
}

export default function PublicVerify() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const v = params.get("v") || "";
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/verify/${code}`, { params: { v } })
      .then(({ data }) => setState({ status: "ok", data }))
      .catch((err) => setState({ status: "error", err: err?.response?.data?.detail || "Error" }));
  }, [code, v]);

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="public-verify-page">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo size={32} withText />
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Inicio
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-xl">
          <CardContent className="p-8 space-y-5">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">Verificación de documento</div>
              <Badge variant="outline">Código: {code}</Badge>
            </div>

            {state.status === "loading" && (
              <div className="flex flex-col items-center py-10 gap-3 text-muted-foreground">
                <Loader2 className="w-10 h-10 animate-spin text-primary" />
                <div>Verificando...</div>
              </div>
            )}

            {state.status === "ok" && state.data?.valid && (
              <div className="space-y-4" data-testid="verify-valid">
                <div className="flex items-center gap-3 p-4 rounded-xl bg-[#E8F1E5] border border-[#B7CDB1]">
                  <CheckCircle2 className="w-8 h-8 text-[#4A6E4F]" />
                  <div>
                    <div className="font-display text-lg">Documento auténtico</div>
                    <div className="text-sm text-muted-foreground">{docSubtitle(state.data)}</div>
                  </div>
                </div>
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <div><dt className="text-muted-foreground">Médico</dt><dd className="font-medium">{state.data.doctor_name}</dd></div>
                  <div><dt className="text-muted-foreground">Especialidad</dt><dd>{state.data.doctor_specialty || "-"}</dd></div>
                  <div><dt className="text-muted-foreground">Nº Colegiado</dt><dd>{state.data.doctor_license || "-"}</dd></div>
                  <div><dt className="text-muted-foreground">Colegio</dt><dd>{state.data.doctor_college || "-"} {state.data.doctor_region ? `(${state.data.doctor_region})` : ""}</dd></div>
                  <div><dt className="text-muted-foreground">Paciente</dt><dd>{state.data.patient_name}</dd></div>
                  <div><dt className="text-muted-foreground">Emitida</dt><dd>{new Date(state.data.created_at).toLocaleDateString("es-ES")}</dd></div>
                  {state.data.valid_until && <div><dt className="text-muted-foreground">Válida hasta</dt><dd>{state.data.valid_until}</dd></div>}
                  {typeof state.data.medications_count === "number" && (
                    <div><dt className="text-muted-foreground">Medicamentos</dt><dd>{state.data.medications_count}</dd></div>
                  )}
                  {typeof state.data.tests_count === "number" && (
                    <div><dt className="text-muted-foreground">Pruebas</dt><dd>{state.data.tests_count}</dd></div>
                  )}
                  {state.data.title && (
                    <div className="col-span-2"><dt className="text-muted-foreground">Título</dt><dd className="font-medium">{state.data.title}</dd></div>
                  )}
                  {state.data.start_date && (
                    <div><dt className="text-muted-foreground">Fecha inicio</dt><dd>{state.data.start_date}</dd></div>
                  )}
                  {state.data.end_date && (
                    <div><dt className="text-muted-foreground">Fecha fin</dt><dd>{state.data.end_date}</dd></div>
                  )}
                </dl>
                {state.data.signature && (
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs space-y-1">
                    <div className="flex items-center gap-2 font-medium text-emerald-800">
                      <ShieldCheck className="w-4 h-4" /> Firma electrónica simple (eIDAS)
                    </div>
                    <div className="text-emerald-900">Sellada: {new Date(state.data.signature.signed_at).toLocaleString("es-ES")}</div>
                    <div className="font-mono text-[11px] text-emerald-900">Hash: {state.data.signature.short}</div>
                    <div className="text-[11px] text-muted-foreground">{state.data.signature.algo}</div>
                  </div>
                )}
              </div>
            )}

            {state.status === "ok" && state.data && !state.data.valid && (
              <div className="space-y-3" data-testid="verify-invalid">
                <div className={`flex items-center gap-3 p-4 rounded-xl ${state.data.reason === "signature_invalid" ? "bg-red-100 border border-red-300" : "bg-red-50 border border-red-200"}`}>
                  {state.data.reason === "signature_invalid" ? (
                    <ShieldAlert className="w-8 h-8 text-red-700" />
                  ) : (
                    <XCircle className="w-8 h-8 text-destructive" />
                  )}
                  <div>
                    <div className="font-display text-lg">
                      {state.data.reason === "signature_invalid" ? "¡Documento alterado!" : "Documento no válido"}
                    </div>
                    <div className="text-sm text-muted-foreground">{invalidReasonLabel(state.data.reason)}</div>
                  </div>
                </div>
                {state.data.reason === "signature_invalid" && state.data.signature && (
                  <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs">
                    <div className="font-medium text-red-800">Firma electrónica inválida</div>
                    <div className="text-red-900">Sellada originalmente: {new Date(state.data.signature.signed_at).toLocaleString("es-ES")}</div>
                    <div className="text-red-900">El contenido del documento no coincide con su firma. No confíe en este documento.</div>
                  </div>
                )}
              </div>
            )}

            {state.status === "error" && (
              <div className="text-sm text-destructive" data-testid="verify-error">No se pudo verificar: {state.err}</div>
            )}

            <div className="text-[11px] text-muted-foreground border-t border-border pt-3">
              Esta verificación es pública. Solo se muestra información mínima para validar la autenticidad del documento.
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
